import { Form, useActionData, useNavigation } from "react-router";
import type { SpacePreview } from "@repo/shared-types";
import { formatMonth } from "@/lib/times";
import { Avatar, SpacePicture } from "@/components/avatar";
import { SpaceBadges } from "@/components/space-badges";
import { PageTitle } from "@/components/brand";
import { ErrorNote } from "@/components/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * A public space as somebody outside it sees it, at its own address: how
 * many members and published events it has, who owns it, since when, and
 * Ask to join — or, once asked, Withdraw. Its events, boards and members
 * stay its members'; the api answers this page for a public space alone, so
 * a private one is still "Page not found" (decided with Tiago, 6 October).
 */
export function SpacePreviewPage({ preview, zone }: { preview: SpacePreview; zone: string }) {
  // Asking and withdrawing say so in a toast (the route's action); only a
  // refusal stays on the page.
  const result = useActionData() as { error?: string } | undefined;
  const busy = useNavigation().state === "submitting";
  const { tenant, owner } = preview;

  return (
    <section className="space-y-6">
      <div className="flex min-w-0 items-start gap-4">
        <SpacePicture space={tenant} size="xl" />
        <div className="min-w-0 space-y-2">
          <PageTitle of={[tenant.name]} />
          <h1 className="page-title wrap-anywhere">{tenant.name}</h1>
          <SpaceBadges tenant={tenant} />
          <p className="text-sm text-muted-foreground">Since {formatMonth(preview.createdAt, zone)}</p>
        </div>
      </div>

      {result?.error && <ErrorNote message={result.error} />}

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Card size="sm" className="gap-1 px-(--card-spacing)">
          <dt className="text-sm text-muted-foreground">Members</dt>
          <dd className="text-2xl font-semibold tabular-nums">{preview.members.toLocaleString()}</dd>
        </Card>
        <Card size="sm" className="gap-1 px-(--card-spacing)">
          <dt className="text-sm text-muted-foreground">Events</dt>
          <dd className="text-2xl font-semibold tabular-nums">{preview.events.toLocaleString()}</dd>
        </Card>
        <Card size="sm" className="col-span-2 gap-2 px-(--card-spacing) sm:col-span-1">
          <dt className="text-sm text-muted-foreground">Owner</dt>
          <dd className="flex min-w-0 items-center gap-2">
            <Avatar person={owner} size="md" />
            <span className="min-w-0">
              <span className="block truncate font-medium">{owner.name ?? owner.username}</span>
              <span className="block truncate text-sm text-muted-foreground">@{owner.username}</span>
            </span>
          </dd>
        </Card>
      </dl>

      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4">
          <p className="min-w-0 flex-1 basis-56 text-sm text-muted-foreground">
            {preview.requested
              ? "You asked to join. Somebody there decides next."
              : "You are not in this space. Its events, leaderboards and members are for the people in it."}
          </p>
          <Form method="post">
            <input type="hidden" name="intent" value={preview.requested ? "withdraw" : "join"} />
            {preview.requested ? (
              <Button type="submit" variant="outline" size="lg" disabled={busy}>
                Withdraw
              </Button>
            ) : (
              <Button type="submit" size="lg" disabled={busy}>
                Ask to join
              </Button>
            )}
          </Form>
        </CardContent>
      </Card>
    </section>
  );
}
